// Bonus schedule PDF export (spec §11). Portrait, letter size.
// End times are computed the same way as the app so the PDF always matches the
// screen, including next-day prefixes.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import Utils from '../../Utils/Utils';
import { formatTime, computeEnd, parseHHMM } from './bonusSchedule';
import { DAYS_FULL, DAYS_ABBR } from './tokens';

const INK = [11, 19, 84];
const INDIGO = [74, 91, 217];
const INDIGO_SOFT = [238, 240, 254];
const MUTED = [122, 128, 155];
const GREEN = [22, 163, 74];
const RED = [220, 38, 38];
const AMBER = [255, 201, 60];
const LINE = [225, 228, 242];
const TEXT = [28, 32, 52];

// Per-day schedule rows for one block. ASCII only (built-in PDF fonts can't
// render the "→" glyph, so we use a two-column day / time layout instead).
function scheduleRows(block) {
  const days = (block.days || []).slice().sort((a, b) => a - b);
  if (block.allDay) {
    return days.map((d) => ({
      day: DAYS_FULL[d],
      value: 'All day',
      allDay: true,
    }));
  }
  const startMin = parseHHMM(block.startTime);
  const dur = block.durationMinutes;
  if (startMin == null || dur == null) return [];
  return days.map((d) => {
    const { endDay, endMin, nextDay } = computeEnd(d, startMin, dur);
    const end = nextDay
      ? `${DAYS_ABBR[endDay]} ${formatTime(endMin)}`
      : formatTime(endMin);
    return { day: DAYS_FULL[d], value: `${formatTime(startMin)}  -  ${end}` };
  });
}

export function downloadBonusSchedulePdf(bonuses, locationName) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'letter',
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 44;
  const contentW = pageWidth - margin * 2;

  const stamp = new Date().toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  // Draw a letter-spaced label without leaking char spacing into later text.
  const spacedLabel = (text, x, yy, space) => {
    doc.setCharSpace(space);
    doc.text(text, x, yy);
    doc.setCharSpace(0);
  };

  // ── Header band (page 1) ────────────────────────────────────────────────
  doc.setFillColor(...INK);
  doc.rect(0, 0, pageWidth, 92, 'F');
  doc.setFillColor(...AMBER);
  doc.rect(0, 92, pageWidth, 3, 'F');

  doc.setTextColor(...INDIGO_SOFT);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  spacedLabel('BONUS SCHEDULE', margin, 34, 2);

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.text(locationName || 'Location', margin, 60);

  doc.setTextColor(200, 205, 230);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Generated ${stamp}`, margin, 78);

  const activeCount = bonuses.filter((b) => b.enabled).length;
  doc.setTextColor(255, 255, 255);
  doc.text(
    `${activeCount} active  ·  ${bonuses.length} total`,
    pageWidth - margin,
    60,
    {
      align: 'right',
    }
  );

  let y = 122;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - 48) {
      doc.addPage();
      y = margin;
    }
  };

  const drawChip = (text, xRight, baseline, fill) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    const w = doc.getTextWidth(text) + 16;
    const x = xRight - w;
    doc.setFillColor(...fill);
    doc.roundedRect(x, baseline - 11, w, 15, 4, 4, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text(text, x + w / 2, baseline - 0.5, { align: 'center' });
  };

  const renderBonus = (b) => {
    ensureSpace(70);

    // Name + status chip
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12.5);
    doc.setTextColor(...INK);
    doc.text(b.name || '(unnamed)', margin, y);
    drawChip(
      b.enabled ? 'ACTIVE' : 'INACTIVE',
      pageWidth - margin,
      y,
      b.enabled ? GREEN : RED
    );
    y += 8;
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.6);
    doc.line(margin, y, pageWidth - margin, y);
    y += 12;

    // Payout table
    const payoutRows = (b.specifications || []).map((p) => [
      p.description || '',
      `$${Number(p.amount || 0).toFixed(2)}`,
    ]);
    if (payoutRows.length > 0) {
      autoTable(doc, {
        startY: y,
        head: [['DESCRIPTION', 'PAY TO CUSTOMER']],
        body: payoutRows,
        theme: 'striped',
        margin: { left: margin, right: margin },
        tableWidth: contentW,
        headStyles: {
          fillColor: INDIGO_SOFT,
          textColor: INK,
          fontStyle: 'bold',
          fontSize: 7.5,
          cellPadding: { top: 5, bottom: 5, left: 8, right: 8 },
        },
        bodyStyles: { fontSize: 9.5, textColor: TEXT, cellPadding: 6 },
        alternateRowStyles: { fillColor: [249, 250, 253] },
        columnStyles: {
          0: { fontStyle: 'bold' },
          1: { cellWidth: 130, fontStyle: 'bold', textColor: INK },
        },
        // Right-align the amount column — header ("PAY TO CUSTOMER") and cells —
        // so the label sits directly over the values.
        didParseCell: (data) => {
          if (data.column.index === 1) data.cell.styles.halign = 'right';
        },
      });
      y = doc.lastAutoTable.finalY + 14;
    }

    // Schedule — day (left) / time window (right)
    const rows = (b.blocks || []).reduce(
      (acc, blk) => acc.concat(scheduleRows(blk)),
      []
    );
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    spacedLabel('SCHEDULE', margin, y, 1.2);
    y += 20;

    if (rows.length === 0) {
      ensureSpace(15);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9.5);
      doc.setTextColor(...MUTED);
      doc.text('No schedule set.', margin + 11, y);
      y += 15;
    } else {
      rows.forEach((r) => {
        ensureSpace(15);
        doc.setFillColor(...INDIGO);
        doc.circle(margin + 2.5, y - 3, 1.5, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(...INK);
        doc.text(r.day, margin + 11, y);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...(r.allDay ? GREEN : TEXT));
        doc.text(r.value, pageWidth - margin, y, { align: 'right' });
        y += 15;
      });
    }
    y += 28;
  };

  const active = bonuses.filter((b) => b.enabled);
  const inactive = bonuses.filter((b) => !b.enabled);

  active.forEach(renderBonus);

  if (inactive.length > 0) {
    ensureSpace(44);
    y += 2;
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.6);
    doc.line(margin, y, pageWidth - margin, y);
    y += 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    spacedLabel('INACTIVE BONUSES', margin, y, 1);
    y += 16;
    inactive.forEach(renderBonus);
  }

  // ── Footer on every page ────────────────────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i += 1) {
    doc.setPage(i);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.6);
    doc.line(margin, pageHeight - 34, pageWidth - margin, pageHeight - 34);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text('Generated by NuVe System', margin, pageHeight - 20);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 20, {
      align: 'right',
    });
  }

  const safeName = (locationName || 'location')
    .replace(/[^a-z0-9]+/gi, '-')
    .toLowerCase();

  // Route through the shared bridge (same as Customer Loan Report): on the
  // mobile/tablet app this opens the native PDF viewer with print + share;
  // on the web it downloads the file.
  const pdfData = doc.output('arraybuffer');
  Utils.handlePdfAction(pdfData, 'download', `bonus-schedule-${safeName}.pdf`);
}
